import React, { useEffect, useMemo, useState } from "react";

import { fetchJsonWithAuth } from "../api/client";
import { useAdminAuth } from "../auth/AdminAuthProvider";
import { AdminProtectedRoute } from "../auth/AdminProtectedRoute";
import { AdminShell } from "../components/AdminShell";
import { LoadingLine, Modal, NoticeBanner, Pager, SectionCard } from "../components/ui";
import { useSiteConfigPreview } from "../hooks/useSiteConfigPreview";
import { buildCacheKey, getFreshCache, setCache } from "../shared/adminCache";
import { downloadCsvFile, toYoutubeEmbed } from "../shared/helpers";
import { SiteConfigResponse, SiteConfigSnapshotRow } from "../shared/siteContent";

type Notice = { type: "ok" | "warn" | "error"; message: string } | null;
const ADMIN_CACHE_TTL_MS = 2 * 60 * 1000;

// site_config API 응답에서 화면이 요구하는 핵심 필드만 안전하게 추출한다.
function normalizeSiteConfigResponse(siteConfig: Partial<SiteConfigResponse> | null | undefined) {
  if (!siteConfig || typeof siteConfig !== "object") {
    return {
      sections: null,
      meta: null,
      usedFallback: true,
    };
  }

  return {
    sections: siteConfig.sections || null,
    meta: siteConfig.meta || null,
    usedFallback: false,
  };
}

// site_config sections/meta를 스냅샷 테이블 표시용 행 목록으로 변환한다.
function buildSnapshotRows(response: SiteConfigResponse): SiteConfigSnapshotRow[] {
  const sections = response.sections;
  const meta = response.meta;
  const rows: SiteConfigSnapshotRow[] = [];

  rows.push({
    rowId: "popup",
    key: "popup",
    displayName: "popup",
    version: Number(meta.popup?.version || 0),
    updatedAt: String(meta.popup?.updated_at || ""),
    updatedByDisplay: String(meta.popup?.updated_by_display || "-"),
    createdAt: String(meta.popup?.created_at || ""),
    previewPayload: sections.popup,
  });
  rows.push({
    rowId: "history",
    key: "history",
    displayName: "history",
    version: Number(meta.history?.version || 0),
    updatedAt: String(meta.history?.updated_at || ""),
    updatedByDisplay: String(meta.history?.updated_by_display || "-"),
    createdAt: String(meta.history?.created_at || ""),
    previewPayload: sections.history,
  });
  rows.push({
    rowId: "youtube",
    key: "youtube",
    displayName: "youtube",
    version: Number(meta.youtube?.version || 0),
    updatedAt: String(meta.youtube?.updated_at || ""),
    updatedByDisplay: String(meta.youtube?.updated_by_display || "-"),
    createdAt: String(meta.youtube?.created_at || ""),
    previewPayload: sections.youtube,
  });

  // IR 목록은 published_at(YYYY.MM) 기준 최신순으로 정렬해 행에 펼친다.
  const irItems = sections.ir_ko.items.slice().sort((a, b) => {
    // YYYY.MM 형식을 정렬 가능한 숫자 랭크(YYYYMM)로 변환한다.
    const toRank = (value: string | null | undefined) => {
      const text = String(value || "").trim();
      const match = /^(\d{4})\.(\d{2})$/.exec(text);
      if (!match) return -1;
      const year = Number(match[1]);
      const month = Number(match[2]);
      if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return -1;
      return year * 100 + month;
    };
    return toRank(String(b.published_at || "")) - toRank(String(a.published_at || ""));
  });

  irItems.forEach((item, index) => {
    rows.push({
      rowId: `ir_ko:${item.id || index}`,
      key: "ir_ko",
      displayName: `ir_ko (${item.name || item.id || index + 1})`,
      version: Number(meta.ir_ko?.version || 0),
      updatedAt: String(meta.ir_ko?.updated_at || ""),
      updatedByDisplay: String(meta.ir_ko?.updated_by_display || "-"),
      createdAt: String(meta.ir_ko?.created_at || ""),
      previewPayload: item,
    });
  });

  return rows;
}

/**
 * 최근 사이트 설정 내역 페이지.
 * key별 메타 스냅샷 조회/캐시 동기화/미리보기 모달을 제공한다.
 */
export function ConfigPage() {
  const { getIdToken, ready, user } = useAdminAuth();
  const [notice, setNotice] = useState<Notice>(null);
  const [rows, setRows] = useState<SiteConfigSnapshotRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [historySearch, setHistorySearch] = useState("");
  const [historyPreviewPage, setHistoryPreviewPage] = useState(1);
  const preview = useSiteConfigPreview();
  const pageSize = 10;

  const pagedRows = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * pageSize;
    return {
      rows: rows.slice(start, start + pageSize),
      page: safePage,
      totalPages,
    };
  }, [page, rows]);

  // cache-first + background sync 전략으로 site_config 스냅샷을 로드한다.
  async function load(options?: { forceNetwork?: boolean }) {
    const forceNetwork = Boolean(options?.forceNetwork);
    const cacheKey = user ? buildCacheKey(user.uid, "config") : "";
    let hasWarmData = false;
    setNotice(null);
    if (!forceNetwork && cacheKey) {
      const cached = getFreshCache<{ rows: SiteConfigSnapshotRow[] }>(cacheKey, ADMIN_CACHE_TTL_MS);
      if (cached?.data?.rows) {
        setRows(cached.data.rows);
        setPage(1);
        hasWarmData = true;
      }
    }
    setLoading(!hasWarmData);
    setSyncing(hasWarmData);
    if (hasWarmData) {
      setNotice({ type: "warn", message: "서버 최신 정보를 동기화 중입니다." });
    }
    try {
      const data = await fetchJsonWithAuth<SiteConfigResponse>("/api/admin/site-config", { method: "GET" }, getIdToken);
      const normalized = normalizeSiteConfigResponse(data);
      if (!normalized.sections || !normalized.meta) {
        setRows([]);
        setNotice({ type: "warn", message: "site_config 응답이 비어 있습니다." });
      } else {
        const nextRows = buildSnapshotRows(data);
        setRows(nextRows);
        setPage(1);
        if (cacheKey) {
          setCache<{ rows: SiteConfigSnapshotRow[] }>(cacheKey, { rows: nextRows });
        }
        setNotice(null);
      }
    } catch (caught) {
      if (hasWarmData) {
        setNotice({ type: "warn", message: "캐시 데이터를 표시 중이며 최신 동기화에 실패했습니다." });
      } else {
        setNotice({ type: "error", message: caught instanceof Error ? caught.message : "site_config 조회에 실패했습니다." });
      }
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }

  useEffect(() => {
    if (!ready || !user) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user?.uid]);

  // 현재 스냅샷 목록을 CSV로 내보낸다.
  function exportCsv() {
    const headers = ["key", "version", "updated_at", "관리자", "created_at"];
    const csvRows = rows.map((row) => [row.displayName, row.version, row.updatedAt, row.updatedByDisplay, row.createdAt]);
    downloadCsvFile(`site_config_snapshot_${Date.now()}.csv`, headers, csvRows);
  }

  const historyPreview = useMemo(() => {
    if (!preview.currentRow || preview.currentRow.key !== "history") return { rows: [], totalPages: 1, page: 1 };
    const list = Array.isArray(preview.currentRow.previewPayload) ? preview.currentRow.previewPayload : [];
    const query = historySearch.trim().toLowerCase();
    const filtered = query
      ? list.filter((row) => `${String((row as { year?: string }).year || "")} ${String(((row as { contents?: string[] }).contents || []).join(" "))}`.toLowerCase().includes(query))
      : list.slice();
    const sorted = filtered.sort((a, b) => String((b as { year?: string }).year || "").localeCompare(String((a as { year?: string }).year || "")));
    const totalPages = Math.max(1, Math.ceil(sorted.length / 10));
    const safePage = Math.min(Math.max(1, historyPreviewPage), totalPages);
    const start = (safePage - 1) * 10;
    return {
      rows: sorted.slice(start, start + 10),
      totalPages,
      page: safePage,
    };
  }, [historyPreviewPage, historySearch, preview.currentRow]);

  return (
    <AdminProtectedRoute>
      <AdminShell title="최근 사이트 설정 내역" description="site_config 스냅샷을 읽기 전용으로 조회하고 key별 미리보기를 제공합니다.">
        <NoticeBanner notice={notice} />

        <div className="cm-toolbar">
          <button className="cm-btn cm-btn-refresh" disabled={loading} onClick={() => void load({ forceNetwork: true })} type="button">
            새로고침
          </button>
          <button className="cm-btn cm-btn-secondary" onClick={exportCsv} type="button">
            CSV 다운로드
          </button>
        </div>

        {loading ? <LoadingLine message="site_config 스냅샷을 불러오는 중입니다..." /> : null}
        {!loading && syncing ? <LoadingLine message="서버 최신 정보를 동기화 중입니다..." /> : null}

        <div className="cm-table-wrap">
          <table className="cm-table">
            <thead>
              <tr>
                <th>key</th>
                <th>version</th>
                <th>updated_at</th>
                <th>관리자</th>
                <th>created_at</th>
                <th>미리보기</th>
              </tr>
            </thead>
            <tbody>
              {!pagedRows.rows.length && !loading ? (
                <tr>
                  <td colSpan={6}>site_config 데이터가 없습니다.</td>
                </tr>
              ) : null}
              {pagedRows.rows.map((row) => (
                <tr key={row.rowId}>
                  <td data-label="key">{row.displayName}</td>
                  <td data-label="version">{row.version}</td>
                  <td data-label="updated_at">{row.updatedAt || "-"}</td>
                  <td data-label="관리자">{row.updatedByDisplay}</td>
                  <td data-label="created_at">{row.createdAt || "-"}</td>
                  <td data-label="미리보기">
                    <button
                      className="cm-btn cm-btn-preview"
                      onClick={() => {
                        setHistorySearch("");
                        setHistoryPreviewPage(1);
                        preview.openPreview(row);
                      }}
                      type="button"
                    >
                      미리보기
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pager page={pagedRows.page} totalPages={pagedRows.totalPages} onPageChange={setPage} />

        <Modal onClose={preview.closePreview} open={preview.isOpen} title={preview.currentRow ? `미리보기: ${preview.currentRow.displayName}` : "미리보기"}>
          {preview.currentRow?.key === "popup" ? (
            <div className="cm-grid-2">
              {(Array.isArray(preview.currentRow.previewPayload) ? preview.currentRow.previewPayload : []).map((popup, index) => {
                const entry = popup as {
                  id?: string;
                  enabled?: boolean;
                  image_storage_path?: string;
                  image_url?: string;
                  start_at?: string;
                  end_at?: string;
                  link_url?: string;
                };
                return (
                  <SectionCard key={entry.id || index} title={entry.id || `popup${index + 1}`}>
                    <div className="cm-inline-row">
                      <span className={`cm-pill ${entry.enabled ? "ok" : "warn"}`.trim()}>{entry.enabled ? "활성" : "비활성"}</span>
                    </div>
                    {entry.image_url ? (
                      <div className="cm-field cm-preview-field">
                        {/* config preview도 편집 화면과 동일한 field 폭/비율 규칙을 재사용한다. */}
                        <label>Preview</label>
                        <div className="cm-fixed-preview-box">
                          <img alt={entry.id || "popup"} className="cm-fixed-preview-image" src={entry.image_url} />
                        </div>
                        <div className="cm-path">storage_path: {entry.image_storage_path || "-"}</div>
                        <div className="cm-path">image_url: {entry.image_url || "-"}</div>
                      </div>
                    ) : (
                      <div className="cm-muted">이미지 없음</div>
                    )}
                    <div className="cm-muted">노출기간: {(entry.start_at || "-") + " ~ " + (entry.end_at || "-")}</div>
                    <div className="cm-path">link: {entry.link_url || "-"}</div>
                  </SectionCard>
                );
              })}
            </div>
          ) : null}

          {preview.currentRow?.key === "youtube" ? (
            <div className="cm-grid-2">
              {(["about_us", "videos"] as const).map((group) => {
                const payload = preview.currentRow?.previewPayload as { about_us?: { urls?: string[] }; videos?: { urls?: string[] } };
                const urls = (payload[group]?.urls || []).filter(Boolean);
                const embedUrls = urls.map((url) => toYoutubeEmbed(url)).filter(Boolean);
                return (
                  <SectionCard key={group} title={group === "about_us" ? "About Us" : "Videos"}>
                    {embedUrls.length ? (
                      <div className="cm-youtube-preview-scroll">
                        {embedUrls.map((embedUrl, index) => (
                          <div className="cm-preview cm-preview-embed" key={`${group}-${embedUrl}-${index}`}>
                            <iframe src={embedUrl} style={{ width: "100%", minHeight: 220, border: 0, borderRadius: 8 }} title={`${group}-preview-${index}`} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="cm-muted">링크 없음</div>
                    )}
                    <ul style={{ margin: "10px 0 0", paddingLeft: 18 }}>
                      {urls.map((url) => (
                        <li key={url} className="cm-path">
                          {url}
                        </li>
                      ))}
                    </ul>
                  </SectionCard>
                );
              })}
            </div>
          ) : null}

          {preview.currentRow?.key === "history" ? (
            <div className="cm-panel-grid">
              <div className="cm-toolbar">
                <input className="cm-input" onChange={(event) => setHistorySearch(event.target.value)} placeholder="연월/내용 검색" value={historySearch} />
              </div>
              {historyPreview.rows.map((row, index) => {
                const entry = row as { id?: string; year?: string; contents?: string[] };
                return (
                  <SectionCard key={entry.id || index} title={entry.year || "-"}>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {(entry.contents || []).map((line, lineIndex) => (
                        <li key={`${entry.id || index}-${lineIndex}`}>{line}</li>
                      ))}
                    </ul>
                  </SectionCard>
                );
              })}
              {!historyPreview.rows.length ? <div className="cm-muted">표시할 연혁이 없습니다.</div> : null}
              <Pager page={historyPreview.page} totalPages={historyPreview.totalPages} onPageChange={setHistoryPreviewPage} />
            </div>
          ) : null}

          {preview.currentRow?.key === "ir_ko" ? (
            <SectionCard title="IR Item">
              {(() => {
                const item = (preview.currentRow?.previewPayload || {}) as {
                  id?: string;
                  code?: string;
                  name?: string;
                  title?: string;
                  published_at?: string;
                  file_name?: string;
                  file_storage_path?: string;
                  file_url?: string;
                };
                return (
                  <div className="cm-panel-grid">
                    <div className="cm-inline-row">
                      <span className="cm-pill">code: {item.code || "-"}</span>
                      <button
                        className="cm-btn cm-btn-preview"
                        disabled={!item.file_url}
                        onClick={() => {
                          if (!item.file_url) return;
                          window.open(item.file_url, "_blank", "noopener,noreferrer");
                        }}
                        type="button"
                      >
                        현재 IR 파일 미리보기
                      </button>
                    </div>
                    <div className="cm-field">
                      <label>name</label>
                      <input className="cm-input" readOnly value={item.name || ""} />
                    </div>
                    <div className="cm-field">
                      <label>title</label>
                      <input className="cm-input" readOnly value={item.title || ""} />
                    </div>
                    <div className="cm-field">
                      <label>published_at</label>
                      <input className="cm-input" readOnly value={item.published_at || ""} />
                    </div>
                    <div className="cm-field">
                      <label>file_name</label>
                      <input className="cm-input" readOnly value={item.file_name || ""} />
                    </div>
                    <div className="cm-path">storage_path: {item.file_storage_path || "-"}</div>
                    <div className="cm-path">file_url: {item.file_url || "-"}</div>
                  </div>
                );
              })()}
            </SectionCard>
          ) : null}
        </Modal>
      </AdminShell>
    </AdminProtectedRoute>
  );
}
